import { useRef, useState } from 'react';
import { compressImage, cropToSquare, isValidImageType, formatFileSize } from '../../utils/imageUtils';

interface PhotoCaptureProps {
  onCapture: (file: File, previewUrl: string) => void;
  onError: (message: string) => void;
  disabled?: boolean;
}

export function PhotoCapture({ onCapture, onError, disabled }: PhotoCaptureProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isValidImageType(file)) {
      onError('Please select a valid image (JPEG, PNG, or WebP)');
      return;
    }

    setIsProcessing(true);

    try {
      const { file: compressedFile, dataUrl } = await compressImage(file);
      console.log(
        `Image compressed: ${formatFileSize(file.size)} → ${formatFileSize(compressedFile.size)}`
      );
      const { file: croppedFile, dataUrl: croppedDataUrl } = await cropToSquare(dataUrl);
      console.log(
        `Image cropped to square: ${formatFileSize(compressedFile.size)} → ${formatFileSize(croppedFile.size)}`
      );
      setPreviewUrl(croppedDataUrl);
      onCapture(croppedFile, croppedDataUrl);
    } catch (error) {
      console.error('Image compression failed:', error);
      onError('Failed to process image. Please try again.');
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openFilePicker = (capture?: 'environment' | 'user') => {
    if (fileInputRef.current) {
      if (capture) {
        fileInputRef.current.setAttribute('capture', capture);
      } else {
        fileInputRef.current.removeAttribute('capture');
      }
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Select plant photo"
      />

      {/* Camera viewfinder */}
      <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-white border border-gray-200">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Plant preview"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="text-center">
              <span
                className="material-symbols-outlined text-gray-300 block"
                style={{ fontSize: 48 }}
                aria-hidden="true"
              >
                center_focus_strong
              </span>
              <p className="mt-3 text-sm font-serif text-gray-400">
                Position your plant in the frame
              </p>
            </div>
          </div>
        )}

        {/* Scanning corner brackets */}
        <div className="pointer-events-none absolute inset-4">
          <div className="absolute left-0 top-0 h-8 w-8">
            <div className="absolute left-0 top-0 h-full w-0.5 bg-primary" />
            <div className="absolute left-0 top-0 h-0.5 w-full bg-primary" />
          </div>
          <div className="absolute right-0 top-0 h-8 w-8">
            <div className="absolute right-0 top-0 h-full w-0.5 bg-primary" />
            <div className="absolute right-0 top-0 h-0.5 w-full bg-primary" />
          </div>
          <div className="absolute bottom-0 left-0 h-8 w-8">
            <div className="absolute bottom-0 left-0 h-full w-0.5 bg-primary" />
            <div className="absolute bottom-0 left-0 h-0.5 w-full bg-primary" />
          </div>
          <div className="absolute bottom-0 right-0 h-8 w-8">
            <div className="absolute bottom-0 right-0 h-full w-0.5 bg-primary" />
            <div className="absolute bottom-0 right-0 h-0.5 w-full bg-primary" />
          </div>
        </div>

        {/* Processing overlay */}
        {isProcessing && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <div className="text-center">
              <span
                className="material-symbols-outlined text-primary animate-pulse block"
                style={{ fontSize: 32 }}
                aria-hidden="true"
              >
                hourglass_top
              </span>
              <p className="mt-3 text-sm font-sans text-gray-500">Processing...</p>
            </div>
          </div>
        )}
      </div>

      {/* Camera button */}
      <button
        type="button"
        onClick={() => openFilePicker('environment')}
        disabled={disabled || isProcessing}
        className="btn-editorial group disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
          photo_camera
        </span>
        <span>Take a photo</span>
      </button>

      {/* Gallery button */}
      <button
        type="button"
        onClick={() => openFilePicker()}
        disabled={disabled || isProcessing}
        className="flex w-full h-12 items-center justify-center gap-2 rounded-lg border border-gray-300 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
          photo_library
        </span>
        <span>Choose from gallery</span>
      </button>
    </div>
  );
}
